export interface UnsecuredApplicationCookiePayload {
    appId?: string;
    productCode?: string;
    sessionId?: string;
    offerId?: string;
    clientId?: string;
}

/** Same as aggregators COOKIE_MAX_AGE_24_HOURS – keep when updating cookie so expiration does not change. */
const COOKIE_MAX_AGE_24_HOURS = 86_400;

/** UTF-8 safe base64 encode without deprecated escape/unescape APIs. */
function toBase64Utf8(value: string): string {
    const utf8PercentEncoded = encodeURIComponent(value);
    const binary = utf8PercentEncoded.replace(
        /%([0-9A-F]{2})/g,
        (_, hex: string) => String.fromCharCode(parseInt(hex, 16)),
    );
    return btoa(binary);
}

/** UTF-8 safe base64 decode without deprecated escape/unescape APIs. */
function fromBase64Utf8(value: string): string {
    const binary = atob(value);
    const percentEncoded = Array.from(binary)
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join('');
    return decodeURIComponent(percentEncoded);
}

/** Encode payload in the same format as Remix createCookie (no signing). */
function encodeUnsecuredCookieValue(
    payload: UnsecuredApplicationCookiePayload,
): string {
    return toBase64Utf8(JSON.stringify(payload));
}

/**
 * Decode cookie value: supports Remix format (plain base64) and legacy "j:"+base64.
 */
function decodeUnsecuredCookieValue(
    encodedValue: string,
): UnsecuredApplicationCookiePayload | null {
    try {
        const decoded = decodeURIComponent(encodedValue);
        if (decoded.startsWith('j:')) {
            const jsonString = atob(decoded.slice(2));
            return JSON.parse(jsonString) as UnsecuredApplicationCookiePayload;
        }
        try {
            // Preferred path: Remix-style base64-encoded JSON
            const jsonString = fromBase64Utf8(decoded);
            return JSON.parse(jsonString) as UnsecuredApplicationCookiePayload;
        } catch {
            // Fallback: support legacy/plain JSON cookies used in older tests
            return JSON.parse(decoded) as UnsecuredApplicationCookiePayload;
        }
    } catch {
        return null;
    }
}

const getUnsecuredApplicationCookie =
    (): UnsecuredApplicationCookiePayload | null => {
        if (typeof document === 'undefined') {
            return null;
        }

        try {
            const cookie = document.cookie
                .split('; ')
                .find((row) => row.startsWith('unsecuredApplication='));

            if (!cookie) {
                return null;
            }

            const encodedValue = cookie.split('=').slice(1).join('=');
            return decodeUnsecuredCookieValue(encodedValue);
        } catch {
            return null;
        }
    };

export const getCookieIds = (): {
    appId: string | null;
    sessionId: string | null;
    offerId: string | null;
    clientId: string | null;
} => {
    const cookie = getUnsecuredApplicationCookie();

    return {
        appId: cookie?.appId ?? null,
        sessionId: cookie?.sessionId ?? null,
        offerId: cookie?.offerId ?? null,
        clientId: cookie?.clientId ?? null,
    };
};

type UnsecuredSessionsEntry = {
    sessionId: string;
    offerId?: string;
    clientId?: string;
    /**
     * ISO timestamp indicating when the associated unsecuredApplication cookie
     * is expected to expire. Written by mf-aggregators and mirrored when this
     * app updates the unsecuredApplication cookie so the expiry stays aligned.
     */
    expiryDate?: string;
};

export type UnsecuredSessionsMap = Record<string, UnsecuredSessionsEntry>;

export const getUnsecuredSessions = (): UnsecuredSessionsMap | null => {
    if (typeof document === 'undefined') {
        return null;
    }

    try {
        const cookie = document.cookie
            .split('; ')
            .find((row) => row.startsWith('unsecuredAppSessions='));

        if (!cookie) {
            return null;
        }

        const encodedValue = cookie.split('=').slice(1).join('=');
        const decodedValue = decodeURIComponent(encodedValue);

        return JSON.parse(decodedValue) as UnsecuredSessionsMap;
    } catch {
        return null;
    }
};

/**
 * Cookie attributes aligned with mf-aggregators getUnsecuredCookieSettings
 * (sameSite, secure, path, optional domain) using only an Expires attribute
 * for lifetime (no max-age).
 *
 * When an explicit expiryDate is provided (from unsecuredAppSessions),
 * we emit an Expires attribute matching that timestamp so the updated
 * unsecuredApplication cookie does not outlive the original.
 * Otherwise we compute a 24-hour expiry from \"now\".
 */
function isLocalhost(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    const { hostname } = window.location;
    return hostname === 'localhost' || hostname.endsWith('.localhost');
}

function resolveCookieExpiry(expiryDate?: string): Date | null {
    const effectiveExpiry =
        expiryDate ??
        new Date(Date.now() + COOKIE_MAX_AGE_24_HOURS * 1000).toISOString();
    const date = new Date(effectiveExpiry);
    return Number.isNaN(date.getTime()) ? null : date;
}

function getUnsecuredCookieAttributes(expiryDate?: string): string {
    const attributes = ['path=/', 'SameSite=Lax'];
    const expiry = resolveCookieExpiry(expiryDate);
    const domain = (window as any)._env_?.SELINA_DOMAIN as string | undefined;

    if (expiry) {
        attributes.push(`Expires=${expiry.toUTCString()}`);
    }
    if (!isLocalhost()) {
        attributes.push('Secure');
    }
    if (domain) {
        attributes.push(`domain=${domain}`);
    }

    return attributes.join('; ');
}

/**
 * Set/update the unsecuredApplication cookie to match the format and
 * attributes used by mf-aggregators (set-session-cookie route). Use from
 * this app when it needs to update the cookie (e.g. after switching to a
 * concurrent application).
 */
export const setUnsecuredApplicationCookie = (
    appId: string,
    sessionId: string,
    expiryDate?: string,
    offerId?: string,
    clientId?: string,
) => {
    if (typeof document === 'undefined') {
        return;
    }

    const payload: UnsecuredApplicationCookiePayload = {
        appId,
        sessionId,
        offerId,
        clientId,
    };

    const cookieValue = encodeUnsecuredCookieValue(payload);
    const attributes = getUnsecuredCookieAttributes(expiryDate);

    document.cookie = `unsecuredApplication=${cookieValue}; ${attributes}`;
};
